/**
 * organization-view
 * Enhanced for @bettercone/ui
 * 
 * Organization management view with tab-based navigation for settings, members, and API keys.
 * Follows the same pattern as AccountView with responsive sidebar/drawer navigation.
 */

"use client"

import { MenuIcon } from "lucide-react"
import { useContext, useMemo } from "react"
import { useAuthenticate } from "../../hooks/use-authenticate"
import { AuthUIContext } from "../../lib/auth-ui-provider"
import { cn, getViewByPath } from "../../lib/utils"
import type { AuthLocalization } from "../../localization/auth-localization"
import type { OrganizationViewPath } from "../../lib/view-paths"
import { Button } from "../ui/button"
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "../ui/drawer"
import { Label } from "../ui/label"
import type { SettingsCardClassNames } from "../settings/shared/settings-card"
import { ApiKeysCard } from "../apiKey/api-keys-card"
import { OrganizationSettingsCards } from "./organization-settings-cards"
import { OrganizationMembersCard } from "./organization-members-card"
import { OrganizationInvitationsCard } from "./organization-invitations-card"

export interface OrganizationViewProps {
    className?: string
    classNames?: {
        base?: string
        cards?: string
        drawer?: { menuItem?: string }
        sidebar?: { base?: string; button?: string; buttonActive?: string }
        card?: SettingsCardClassNames
    }
    localization?: AuthLocalization
    path?: string
    pathname?: string
    view?: OrganizationViewPath
    hideNav?: boolean
    slug?: string
}

export function OrganizationView({
    className,
    classNames,
    localization: localizationProp,
    path: pathProp,
    pathname,
    view: viewProp,
    hideNav,
    slug
}: OrganizationViewProps) {
    const {
        apiKey,
        localization: contextLocalization,
        organization: organizationOptions,
        Link
    } = useContext(AuthUIContext)

    useAuthenticate()

    const localization = useMemo(
        () => ({ ...contextLocalization, ...localizationProp }),
        [contextLocalization, localizationProp]
    )

    const path = pathProp ?? pathname?.split("/").pop()

    const view =
        viewProp || getViewByPath(organizationOptions?.viewPaths, path!) || "SETTINGS"

    const navItems: {
        view: OrganizationViewPath
        label: string
    }[] = [
        { view: "SETTINGS", label: localization.SETTINGS },
        { view: "MEMBERS", label: localization.MEMBERS }
    ]

    if (apiKey) {
        navItems.push({
            view: "API_KEYS",
            label: localization.API_KEYS
        })
    }

    return (
        <div
            className={cn(
                "flex w-full grow flex-col gap-4 md:flex-row md:gap-12",
                className,
                classNames?.base
            )}
        >
            {!hideNav && (
                <div className="flex justify-between gap-2 md:hidden">
                    <Label className="font-semibold text-base">
                        {navItems.find((i) => i.view === view)?.label}
                    </Label>

                    <Drawer>
                        <DrawerTrigger asChild>
                            <Button variant="outline">
                                <MenuIcon />
                            </Button>
                        </DrawerTrigger>
                        <DrawerContent>
                            <DrawerHeader>
                                <DrawerTitle className="hidden">
                                    {localization.SETTINGS}
                                </DrawerTitle>
                            </DrawerHeader>
                            <div className="flex flex-col px-4 pb-4">
                                {navItems.map((item) => (
                                    <Link
                                        key={item.view}
                                        href={`${organizationOptions?.basePath}/${slug}/${organizationOptions?.viewPaths[item.view]}`}
                                    >
                                        <Button
                                            size="lg"
                                            className={cn(
                                                "w-full justify-start px-4 transition-none",
                                                classNames?.drawer?.menuItem,
                                                view === item.view
                                                    ? "font-semibold"
                                                    : "text-foreground/70"
                                            )}
                                            variant="ghost"
                                        >
                                            {item.label}
                                        </Button>
                                    </Link>
                                ))}
                            </div>
                        </DrawerContent>
                    </Drawer>
                </div>
            )}

            {!hideNav && (
                <div className="hidden md:block">
                    <div
                        className={cn(
                            "flex w-48 flex-col gap-1 lg:w-60",
                            classNames?.sidebar?.base
                        )}
                    >
                        {navItems.map((item) => (
                            <Link
                                key={item.view}
                                href={`${organizationOptions?.basePath}/${slug}/${organizationOptions?.viewPaths[item.view]}`}
                            >
                                <Button
                                    size="lg"
                                    className={cn(
                                        "w-full justify-start px-4 transition-none",
                                        classNames?.sidebar?.button,
                                        view === item.view
                                            ? "font-semibold"
                                            : "text-foreground/70",
                                        view === item.view &&
                                            classNames?.sidebar?.buttonActive
                                    )}
                                    variant="ghost"
                                >
                                    {item.label}
                                </Button>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {view === "SETTINGS" && (
                <OrganizationSettingsCards
                    classNames={classNames}
                    localization={localization}
                    slug={slug}
                />
            )}

            {view === "MEMBERS" && (
                <div className="grid w-full gap-4 md:gap-6">
                    <OrganizationMembersCard
                        classNames={classNames?.card}
                        localization={localization}
                        slug={slug}
                    />

                    <OrganizationInvitationsCard
                        classNames={classNames?.card}
                        localization={localization}
                        slug={slug}
                    />
                </div>
            )}

            {view === "API_KEYS" && apiKey && (
                <ApiKeysCard
                    classNames={classNames?.card}
                    localization={localization}
                />
            )}
        </div>
    )
}
